import { Row, Col } from '@qc/particle/lib/components/grid';
import Typography from '@qc/particle/lib/components/typography';
import React from 'react';
import OktaSignIn from '@okta/okta-signin-widget/dist/js/okta-sign-in.min.js';
import '@okta/okta-signin-widget/dist/css/okta-sign-in.min.css';
import {oktaConfig} from '../config/okta';
import {setStateToken} from '../oktaConfigUtil';
import { ROUTES } from '../constants/routes';

import * as Stld from './Login.styles';

class LoginView extends React.Component {
  constructor(props){
    super(props);
    const urlParams = new URLSearchParams(props.location.search);
    const loginHint = urlParams.get('login_hint');
    this.state = {
      isCaptchaActive: false,
      captchaEndpoint: '',
      username: loginHint || undefined
    };
    this.oktaWidgetRef = React.createRef();
  }

  handleLoaded = () => {
    const { captchaEndpoint } = this.state
    
    // _DATADOG_SYNTHETICS_BROWSER is set by the bots on datadog.
    // When this happens we still hit the endpoint, for the IP to get checked.
    // The reason is that we have a problem on the recaptcha code not triggering
    // onload event on the tests.
    if (window._DATADOG_SYNTHETICS_BROWSER !== undefined) {
      fetch(captchaEndpoint, {
        method: 'POST',
        body: JSON.stringify({
          version:"v3",
          token: "invalid_token"
        })
      }).then((resp) => {
        if(resp.status !== 200) {
          this.setState({isCaptchaActive:true})
          this.widget.remove();
        } else {
          this.setState({isCaptchaActive:false})
          this.widget.renderEl({el: this.oktaWidgetRef.current});
        }
      });
      return;
    }

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(process.env.REACT_APP_CAPTCHA_SITE_KEY_V3 , { action: "login" })
        .then(token => {
          fetch(captchaEndpoint, {
            method: 'POST',
            body: JSON.stringify({
              version:"v3",
              token: token
              })
          })
         .then((resp) => {
            //200 - API returns a successful result.
            //403 - API returns an unsuccessful result (recaptcha was solved but Google failed to validate the provided token)
            if(resp.status !== 200) {
              this.setState({isCaptchaActive:true})
              this.widget.remove();

              window.grecaptcha.render("reCaptchaV2", {
                'sitekey' : process.env.REACT_APP_CAPTCHA_SITE_KEY_V2,
                'expired-callback': this.recaptchaExpired,
                'callback': (token) => {
                  fetch(captchaEndpoint, {
                    method: 'POST',
                    body: JSON.stringify({
                      version:"v2",
                      token: token
                    })
                  })
                  .then((resp) => {
                    if(resp.status === 200) {
                      this.setState({isCaptchaActive:false})
                      this.widget.renderEl({el: this.oktaWidgetRef.current});
                    }
                  })
                }
              });
            } else {
              this.widget.renderEl({el: this.oktaWidgetRef.current});
            }
           })
          .catch((err) => {
             console.log('error', err)
          })
         })
     })
  }

  recaptchaExpired = () => {
    this.widget.remove();
  }

  async componentDidMount() {
    const testEnv = 'https://test.quantcast.com'

    if(window.location.origin.startsWith('http://localhost') && process.env.REACT_APP_BASE_URL === testEnv) {
      this.setState({captchaEndpoint: `${window.location.origin}/users/api/verify/recaptcha`})
    } else {
      this.setState({captchaEndpoint: `${process.env.REACT_APP_BASE_URL}/users/api/verify/recaptcha`})
    }

    document.title = "Login"
    const script = document.createElement("script")
    script.async= true;
    script.src = `https://google.com/recaptcha/api.js?render=${process.env.REACT_APP_CAPTCHA_SITE_KEY_V3}`
    script.onload = () => {
      this.handleLoaded();
    }
    script.onerror  = () => {
      console.error("Couldn't load recaptcha script!")
    }
    document.body.appendChild(script)
    
    const modifiedOktaConfig = {
      ...oktaConfig,
      username: this.state.username,
      registration: {
        click: () => {
          this.props.history.push({
            pathname: ROUTES.USER_SIGNUP,
            search: this.props.location.search
          })
        }
      }
    }
    setStateToken(modifiedOktaConfig);
    this.widget = new OktaSignIn(modifiedOktaConfig);
    this.widget.on('afterRender', (context) => {
      // upon idp resolution okta widget changes the url and looses the qcRefer state. The below change restores that.
      if (context.controller === 'primary-auth') {
        setTimeout(() => {
          window.history.pushState({}, null, '/user/login' + this.props.location.search ? this.props.location.search : '');
        }, 100);  
      }
      if (context.controller === 'idp-discovery') {
        (document.getElementById('help-links-container') || {style: {}}).style.display = '';
        document.querySelectorAll('[data-se="needhelp"]').forEach(a => a.style.display = 'none');
      }
    });
    await this.refreshAccessToken();
  }

  async refreshAccessToken() {
    return fetch('/token/refreshAccessToken', {method: "POST"});
  }

  componentWillUnmount() {
    this.widget.remove();
  }

  render() {
    const { isCaptchaActive } = this.state;
    const { Paragraph } = Typography;
    
    return (
        <Stld.AuthContainer>
          {isCaptchaActive ? 
            <Stld.BoxCaptcha>
              <div 
                id="reCaptchaV2"
                className="g-recaptcha"
                data-expired-callback="recaptchaExpired"
                data-sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY_V2}
              />
            </Stld.BoxCaptcha> : (
                <Row justify="center">
                    <Col xs={0} md={12} xl={12}>
                      <div className="info-container">
                        <Stld.QuantcastLogo />
                        <h1 className="headline" >Free access to real-time audience insights.</h1>
                        <span className="subheadline">Get started with Quantcast Measure today.</span>
                        <h3>Features</h3>
                        <Paragraph>
                          <ul>
                            <li>
                              <p>Consumer first, consent driven.</p>
                            </li>
                            <li>
                              <p>Audience psychographics at your fingertips.</p>
                            </li>
                            <li>
                              <p>Simple and intuitive interface.</p>
                            </li>
                            <li>
                              <p>Easy implementation.</p>
                            </li>
                          </ul>
                        </Paragraph>
                      </div>
                  </Col>
                  <Col xs={24} md={12} xl={12}>
                    <div ref={this.oktaWidgetRef}/>
                  </Col>
                </Row>
            )
          }
          <div className="g-recaptcha" 
              data-sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY_V3}
              data-size="invisible"
          />
          <Stld.FooterHolder>
            <Stld.Footer>
              <Stld.FooterLink>
                <a href="https://www.quantcast.com/privacy/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  data-cy="privacyLink"
                >
                  Privacy
                </a>
              </Stld.FooterLink>
              <Stld.FooterLink>
                <a href="https://www.quantcast.com/terms/website-terms-use/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  data-cy="termsLink"
                >
                  Terms
                </a>
              </Stld.FooterLink>
            </Stld.Footer>
          </Stld.FooterHolder>
        </Stld.AuthContainer>
    )
  }
}

export default LoginView;
